import React, { useRef, useState, useEffect, useCallback } from "react";
import { motion, useInView, useAnimation, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import HeroSection from "../../components/HeroSection";
import {
  FaUser,
  FaBriefcase,
  FaBuilding,
  FaArrowRight,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import { successStories } from "../../data/successStories";
import { getCollageImages } from "../../utils/heroImages";

const openSans = { fontFamily: "'Open Sans', system-ui, sans-serif" };
const AVATAR_SIZE = 148;
const CARD_WIDTH = 340;
const CARD_GAP = 24;
const AUTO_SCROLL_MS = 4500;
const BREATH_DURATION = 4;
const CARD_SHADOW_REST = "0 4px 14px rgba(30, 64, 175, 0.08)";
const CARD_SHADOW_INHALE = "0 10px 28px rgba(30, 64, 175, 0.16)";
const CARD_STEP = CARD_WIDTH + CARD_GAP;

function getCarouselState(scrollEl, storyCount) {
  if (!scrollEl || storyCount <= 0) {
    return { pageCount: 1, scrollPositions: [0] };
  }

  const maxScroll = Math.max(0, scrollEl.scrollWidth - scrollEl.clientWidth);
  if (maxScroll <= 0) {
    return { pageCount: 1, scrollPositions: [0] };
  }

  const cardsInView = Math.max(
    1,
    Math.floor((scrollEl.clientWidth + CARD_GAP) / CARD_STEP)
  );
  const pageCount = Math.max(1, storyCount - cardsInView + 1);
  const scrollPositions = Array.from({ length: pageCount }, (_, i) =>
    i === pageCount - 1 ? maxScroll : Math.min(i * CARD_STEP, maxScroll)
  );

  return { pageCount, scrollPositions };
}

function getActivePageFromScroll(scrollEl, storyCount) {
  const { pageCount, scrollPositions } = getCarouselState(scrollEl, storyCount);
  if (pageCount <= 1) return 0;

  let nearest = 0;
  let minDistance = Infinity;
  scrollPositions.forEach((pos, i) => {
    const distance = Math.abs(scrollEl.scrollLeft - pos);
    if (distance < minDistance) {
      minDistance = distance;
      nearest = i;
    }
  });
  return nearest;
}

const AnimatedSection = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) controls.start("visible");
  }, [isInView, controls]);

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={{
        hidden: { opacity: 0, y: 24 },
        visible: { opacity: 1, y: 0 },
      }}
      transition={{ duration: 0.5, delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

function ProfileAvatar({ student }) {
  return (
    <div className="flex justify-center pt-6 pb-1">
      <div
        className="rounded-full overflow-hidden bg-white border-[3px] border-white shadow-md ring-2 ring-[#1E40AF]/15"
        style={{ width: AVATAR_SIZE, height: AVATAR_SIZE }}
      >
        {student.image ? (
          <img
            src={student.image}
            alt={student.name}
            className="w-full h-full object-cover"
            style={{ objectPosition: student.imagePosition || "50% 28%" }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-neutral-400">
            <FaUser className="text-3xl" aria-hidden />
          </div>
        )}
      </div>
    </div>
  );
}

function ExperienceList({ experiences }) {
  return (
    <div className="space-y-3 text-left">
      {experiences.map((exp, i) => (
        <div
          key={`${exp.company}-${i}`}
          className="rounded-md bg-white/90 border border-white/80 shadow-sm px-4 py-3"
        >
          <div className="flex items-start gap-3">
            <FaBriefcase className="text-[#1E40AF] mt-0.5 shrink-0" aria-hidden />
            <p className="text-[15px] font-medium text-neutral-900 leading-snug flex-1" style={openSans}>
              {exp.role}
            </p>
          </div>
          <div className="flex items-start gap-3 mt-3 pt-3 border-t border-[#1E40AF]/10">
            <FaBuilding className="text-[#1E40AF] mt-0.5 shrink-0" aria-hidden />
            <p className="text-[15px] font-bold text-black leading-snug flex-1" style={openSans}>
              {exp.company}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

function StoryCard({ student, index }) {
  const reduceMotion = useReducedMotion();
  const experiences =
    student.experiences?.length > 0
      ? student.experiences
      : [{ role: student.role, company: student.company }].filter((e) => e.role || e.company);

  const cardStyle = {
    width: CARD_WIDTH,
    background:
      "linear-gradient(145deg, #DBEAFE 0%, #EFF6FF 28%, #F8FAFC 52%, #FFFFFF 78%, #FFFFFF 100%)",
    boxShadow: CARD_SHADOW_REST,
  };

  return (
    <motion.div
      className="flex-shrink-0 snap-center"
      style={{ width: CARD_WIDTH }}
      initial={reduceMotion ? false : { opacity: 0, y: 20 }}
      whileInView={reduceMotion ? undefined : { opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.45, delay: index * 0.08 }}
    >
      <motion.article
        className="rounded-xl border border-[#1E40AF]/10 overflow-hidden h-full"
        style={cardStyle}
        animate={
          reduceMotion
            ? undefined
            : {
                scale: [1, 1.014, 1],
                boxShadow: [CARD_SHADOW_REST, CARD_SHADOW_INHALE, CARD_SHADOW_REST],
              }
        }
        transition={
          reduceMotion
            ? undefined
            : {
                duration: BREATH_DURATION,
                repeat: Infinity,
                ease: "easeInOut",
                delay: index * 0.5,
              }
        }
      >
        <ProfileAvatar student={student} />
        <div className="px-6 pt-5 pb-6">
          <h3
            className="text-xl font-bold text-black text-center"
            style={{ ...openSans, lineHeight: 1.35 }}
          >
            {student.name}
          </h3>
          <div className="mt-5">
            <ExperienceList experiences={experiences} />
          </div>
          {student.story && (
            <p
              className="text-sm text-neutral-600 mt-4 pt-4 border-t border-[#1E40AF]/10 leading-relaxed"
              style={openSans}
            >
              {student.story}
            </p>
          )}
        </div>
      </motion.article>
    </motion.div>
  );
}

function HorizontalStoriesCarousel({ stories }) {
  const scrollRef = useRef(null);
  const [activePage, setActivePage] = useState(0);
  const [pageCount, setPageCount] = useState(1);
  const [isPaused, setIsPaused] = useState(false);

  const syncPageCount = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return 1;
    const { pageCount: pages } = getCarouselState(el, stories.length);
    setPageCount(pages);
    setActivePage((prev) => Math.min(prev, pages - 1));
    return pages;
  }, [stories.length]);

  const scrollToPage = useCallback(
    (page) => {
      const el = scrollRef.current;
      if (!el) return;
      const { pageCount: pages, scrollPositions } = getCarouselState(el, stories.length);
      const clamped = Math.max(0, Math.min(page, pages - 1));
      el.scrollTo({ left: scrollPositions[clamped], behavior: "smooth" });
      setActivePage(clamped);
      setPageCount(pages);
    },
    [stories.length]
  );

  const scrollBy = (direction) => {
    const el = scrollRef.current;
    const pages = el ? getCarouselState(el, stories.length).pageCount : pageCount;
    const next = activePage + direction;
    if (next < 0) scrollToPage(pages - 1);
    else if (next >= pages) scrollToPage(0);
    else scrollToPage(next);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    syncPageCount();
    const ro = new ResizeObserver(syncPageCount);
    ro.observe(el);
    window.addEventListener("resize", syncPageCount);

    const onScroll = () => {
      const { pageCount: pages } = getCarouselState(el, stories.length);
      const index = getActivePageFromScroll(el, stories.length);
      setPageCount(pages);
      setActivePage(index);
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", syncPageCount);
      el.removeEventListener("scroll", onScroll);
    };
  }, [stories.length, syncPageCount]);

  useEffect(() => {
    if (isPaused || pageCount <= 1) return;
    const timer = setInterval(() => {
      setActivePage((prev) => {
        const el = scrollRef.current;
        if (!el) return prev;
        const { pageCount: pages, scrollPositions } = getCarouselState(el, stories.length);
        const next = prev + 1 >= pages ? 0 : prev + 1;
        el.scrollTo({ left: scrollPositions[next], behavior: "smooth" });
        setPageCount(pages);
        return next;
      });
    }, AUTO_SCROLL_MS);
    return () => clearInterval(timer);
  }, [isPaused, pageCount, stories.length]);

  return (
    <div
      className="relative"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocus={() => setIsPaused(true)}
      onBlur={() => setIsPaused(false)}
    >
      <div className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-10">
        <button
          type="button"
          onClick={() => scrollBy(-1)}
          className="w-11 h-11 rounded-full bg-white border border-gray-200 shadow-md flex items-center justify-center text-[#1E40AF] hover:bg-[#EFF6FF] transition-colors"
          aria-label="Previous story"
        >
          <FaChevronLeft />
        </button>
      </div>
      <div className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-10">
        <button
          type="button"
          onClick={() => scrollBy(1)}
          className="w-11 h-11 rounded-full bg-white border border-gray-200 shadow-md flex items-center justify-center text-[#1E40AF] hover:bg-[#EFF6FF] transition-colors"
          aria-label="Next story"
        >
          <FaChevronRight />
        </button>
      </div>

      <div
        ref={scrollRef}
        className="flex gap-6 overflow-x-auto scroll-smooth px-1 py-4 md:px-14 snap-x snap-mandatory [&::-webkit-scrollbar]:hidden"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {stories.map((student, index) => (
          <StoryCard key={student.id ?? index} student={student} index={index} />
        ))}
      </div>

      {pageCount > 1 && (
        <div className="flex justify-center gap-2 mt-6">
          {Array.from({ length: pageCount }, (_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => scrollToPage(i)}
              className={`h-2 rounded-full transition-all duration-300 ${
                i === activePage ? "w-8 bg-[#1E40AF]" : "w-2 bg-gray-300 hover:bg-gray-400"
              }`}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      )}

      <p className="text-center text-sm text-neutral-500 mt-3" style={openSans}>
        Swipe or use arrows to explore · Auto-advances when idle
      </p>
    </div>
  );
}

export default function SuccessStories() {
  const hasStories = successStories.length > 0;

  return (
    <div className="w-full bg-white">
      <HeroSection
        title="Success Stories"
        subtitle="Meet Ojoto Union alumni and program participants who have turned opportunity into achievement — building careers and giving back to our community."
        buttons={[
          { text: "Apply Now", link: "/register", variant: "blue" },
          { text: "Login", link: "/login", variant: "blue" },
        ]}
        collageImages={getCollageImages("ourStory")}
      />

      <section className="section bg-white">
        <div className="section-container">
          <AnimatedSection>
            <div className="max-w-3xl mx-auto text-center mb-10">
              <h2
                className="text-3xl md:text-4xl font-bold text-black mb-5"
                style={{ ...openSans, letterSpacing: "-0.02em" }}
              >
                Our Graduates, Our Pride
              </h2>
              <p className="text-base md:text-lg text-neutral-600 leading-relaxed" style={openSans}>
                Through scholarships, mentorship, and employability programs, we equip students
                with the skills and networks to succeed. These are their stories.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {hasStories ? (
        <section className="section bg-gray-50 pb-16">
          <div className="section-container max-w-7xl">
            <AnimatedSection delay={0.1}>
              <HorizontalStoriesCarousel stories={successStories} />
            </AnimatedSection>
          </div>
        </section>
      ) : (
        <section className="section bg-gray-50">
          <div className="section-container">
            <AnimatedSection delay={0.2}>
              <div
                className="max-w-xl mx-auto text-center rounded-xl border border-dashed border-gray-300 bg-white px-8 py-12"
                style={openSans}
              >
                <FaUser className="text-4xl text-primary-300 mx-auto mb-4" aria-hidden />
                <p className="text-gray-600 text-lg">
                  Success stories are being added. Check back soon to meet our outstanding
                  graduates.
                </p>
              </div>
            </AnimatedSection>
          </div>
        </section>
      )}

      <section className="section text-center" style={{ backgroundColor: "#F9FAFB" }}>
        <div className="section-container">
          <AnimatedSection>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-black" style={openSans}>
              Start Your Journey
            </h2>
            <p className="text-lg max-w-2xl mx-auto mb-8 text-neutral-600" style={openSans}>
              Join our programs and become the next success story we celebrate.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center justify-center gap-2 px-8 py-3 font-semibold rounded-md transition-colors"
              style={{
                ...openSans,
                fontWeight: 600,
                backgroundColor: "#1E40AF",
                color: "#FFFFFF",
                textDecoration: "none",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#1E3A8A";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#1E40AF";
              }}
            >
              Apply Now
              <FaArrowRight aria-hidden />
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
